<template>
  <BoxPlot
    :title="chartTitle"
    :filterContext="filterContext"
    :dataProviders="dataProviders"
    :contextUpdaters="contextUpdaters"
    :navigationConfig="navigationConfig"
    :chartFontColor="chartFontColor"
    :boxColor="boxColor"
    :dataLoaded="dataLoaded"
  />
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import BoxPlot from '@/components/charts/base/BoxPlot.vue';
import { getSequential } from '@/utils/vizPalette';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();

const filterContext = computed(() => store.state.filterContext);
const chartFontColor = computed(() => store.getters.chartFontColor);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const boxColor = computed(() => getSequential(store.state.theme)[1]);
const conn = store.getters.getDBConnection;

const chartName = 'inspectionIntervalBoxChart';
const chartTitle = t('inspectionInterval');

// The interval is the gap the inspector set between an inspection and the next
// one due, per record. Aggregating it hides thousands of individual Asset IDs
// behind a distribution per Category / Type.
// NOTE: `days` is a reserved word in this DuckDB build - alias as intervalDays.
const INTERVALS_CTE = `
  WITH iv AS (
    SELECT
      assetKey,
      assetRefId,
      assetCategory,
      assetType,
      date_diff('day', effectiveInspectionDate, nextInspectionDate) AS intervalDays
    FROM filteredItems
    WHERE effectiveInspectionDate IS NOT NULL AND nextInspectionDate IS NOT NULL
  )
`;

const STATS = `
  COUNT(*) AS count,
  MIN(intervalDays) AS min,
  quantile_cont(intervalDays, 0.25) AS Q1,
  quantile_cont(intervalDays, 0.5) AS median,
  quantile_cont(intervalDays, 0.75) AS Q3,
  MAX(intervalDays) AS max,
  AVG(intervalDays) AS average,
  COALESCE(STDDEV(intervalDays), 0) AS standard_deviation
`;

const runStats = async (sql) => {
  const result = await conn.query(sql);
  // BoxPlot coerces the stats itself; return the rows as-is.
  return result.toArray().map((row) => row.toJSON());
};

const dataProviders = {
  category: () => runStats(`
    ${INTERVALS_CTE}
    SELECT assetCategory AS title, ${STATS}
    FROM iv
    WHERE intervalDays >= 0 AND assetCategory IS NOT NULL
    GROUP BY assetCategory
    ORDER BY median DESC
  `),
  type: () => runStats(`
    ${INTERVALS_CTE}
    SELECT assetType AS title, assetCategory AS parentId, ${STATS}
    FROM iv
    WHERE intervalDays >= 0 AND assetType IS NOT NULL
    GROUP BY assetType, assetCategory
    ORDER BY median DESC
  `),
  asset: () => runStats(`
    ${INTERVALS_CTE}
    SELECT
      COALESCE(assetRefId, assetKey) AS title,
      assetType AS parentId,
      ${STATS}
    FROM iv
    WHERE intervalDays >= 0
    GROUP BY assetKey, assetRefId, assetType
    ORDER BY median DESC
  `),
};

const applyFilter = (condition, label) =>
  filterContext.value.updateFilterWithCondition(chartName, condition, label);

const contextUpdaters = {
  category: (category) => applyFilter(`assetCategory = ${sqlLiteral(category)}`, `${t('category')}: ${category}`),
  type: (type, category) => applyFilter(
    [`assetType = ${sqlLiteral(type)}`, category ? `assetCategory = ${sqlLiteral(category)}` : null]
      .filter(Boolean).join(' AND '),
    `${t('assetType')}: ${type}`
  ),
  reset: async () => { await filterContext.value.removeFilter(chartName); },
};

const navigationConfig = [
  { level: 'category', titleKey: 'category' },
  { level: 'type', titleKey: 'assetType' },
  { level: 'asset', titleKey: 'assetId' },
];
</script>
